import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { FiEye } from "react-icons/fi";
import { img } from "../Landing/landingContent";

export type SaveStatus = "idle" | "saving" | "saved" | "error";

interface BannerHeaderProps {
  projectName: string;
  sizeLabel: string;
  date: string;
  saveStatus: SaveStatus;
  lastSaved: Date | null;
  onPreview: () => void;
}

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
};

const timeAgo = (from: Date, now: number) => {
  const mins = Math.floor((now - from.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  return from.toLocaleDateString();
};

const BannerHeader: React.FC<BannerHeaderProps> = ({ projectName, sizeLabel, date, saveStatus, lastSaved, onPreview }) => {
  // Re-render every 30s so "Last saved: n mins ago" stays current
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <header className="flex h-16 items-center gap-6 border-b border-gray-200 bg-white px-6 font-jakarta">
      <Link to={(process.env.REACT_APP_PublicPath || "") + "/"} className="shrink-0">
        <img src={img("Logo_B.svg")} alt="BudgetPrintPros" className="h-6 w-auto" />
      </Link>

      <div className="min-w-0 text-xs leading-5 text-ink">
        <div className="truncate">
          <span className="font-semibold">PROJECT</span>
          <span className="mx-1.5 text-gray-400">|</span>
          {projectName}{sizeLabel && ` - ${sizeLabel}`}
        </div>
        <div>
          <span className="font-semibold">Date</span>
          <span className="mx-1.5 text-gray-400">|</span>
          {formatDate(date)}
        </div>
      </div>

      <div className="flex flex-1 justify-center">
        <div className="text-center">
          {saveStatus === "error" ? (
            <div className="flex items-center gap-1.5 text-xs text-red-600">
              <FaExclamationCircle /> Couldn't save — retrying on your next change
            </div>
          ) : saveStatus === "saving" ? (
            <div className="text-xs text-gray-500">Saving…</div>
          ) : lastSaved ? (
            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-700">
              <FaCheckCircle className="text-green-600" /> Auto saved
            </div>
          ) : null}
          {lastSaved && saveStatus !== "saving" && (
            <div className="text-[10px] text-gray-400">Last saved: {timeAgo(lastSaved, now)}</div>
          )}
        </div>
      </div>

      <button onClick={onPreview} className="flex items-center gap-1.5 text-xs font-semibold text-brand hover:text-brand-dark">
        <FiEye /> Preview
      </button>
    </header>
  );
};

export default BannerHeader;
